import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useResponsive } from '@/utils/hooks/useResponsive';
import styled, { css } from 'styled-components';
import Link from '@ui/atoms/Link';
import AushaClubButton from '@app/molecules/AushaClubButton';
import HideFor from '@app/atoms/HideFor';
import Cluster from '@ui/layout/Cluster';
import Text from '@ui/atoms/Text';
import UiKitTooltip from '@ui/atoms/UiKitTooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import UserDropdownMenu from './UserDropdownMenu';
import FeedbackModal from './FeedbackModal';
import OutsideClickHandler from 'react-outside-click-handler';
import useSubscriptionQuery from '@/queries/subscription/useSubscriptionQuery.hook';
import { PRICING } from '@/utils/pricing';
import { useOverlayTriggerState } from 'react-stately';
import AushaClubModal from '../../organisms/AushaClubModal';
import useUserQuery from '@/queries/user/useUserQuery.hook';
import { sendAmplitudeLogEvent } from '@/helpers';

const AushaHeaderMenu = () => {
    const { isMobileOrTablet } = useResponsive();
    const user = useUserQuery();
    const { data: subscription } = useSubscriptionQuery();

    const aushaClubModalState = useOverlayTriggerState({});
    const [dropdownMenuOpened, setDropdownMenuOpened] = useState(false);
    const [feedbackOpened, setFeedbackOpened] = useState();
    const hasUser = user && user.isLoading === false && user.isError === false;

    const handleOpenAushaClubModal = () => {
        if (subscription?.active) return;
        aushaClubModalState.open();
    };

    return (
        <>
            <AushaHeaderMenuWrapper>
                <MenuIconsWraper>
                    <Cluster $align="center" $gap="1rem">
                        <IconsWrapper>
                            {isMobileOrTablet && (
                                <AushaClubButton onClick={handleOpenAushaClubModal} />
                            )}
                            {!isMobileOrTablet && (
                                <>
                                    <UiKitTooltip
                                        variant="dark"
                                        position="bottom"
                                        content={
                                            <FormattedMessage defaultMessage="Bibliothèque musicale" />
                                        }
                                    >
                                        <IconLinkWrapper to="/app/song-library">
                                            <FontAwesomeIcon
                                                icon={icon({
                                                    name: 'list-music',
                                                    style: 'duotone',
                                                })}
                                                color="white"
                                                size="1x"
                                            />
                                        </IconLinkWrapper>
                                    </UiKitTooltip>
                                    <HideFor condition={subscription?.activated}>
                                        <UiKitTooltip
                                            variant="dark"
                                            position="bottom"
                                            content={
                                                <FormattedMessage defaultMessage="Parrainage" />
                                            }
                                        >
                                            <IconLinkWrapper
                                                onClick={() =>
                                                    sendAmplitudeLogEvent(
                                                        'Clicked on Referral Program Icon',
                                                    )
                                                }
                                                to="/app/sponsorship"
                                            >
                                                <FontAwesomeIcon
                                                    icon={icon({ name: 'gift', style: 'duotone' })}
                                                    color="white"
                                                    size="1x"
                                                />
                                            </IconLinkWrapper>
                                        </UiKitTooltip>
                                    </HideFor>
                                    <UiKitTooltip
                                        variant="dark"
                                        position="bottom"
                                        content={<FormattedMessage defaultMessage="Nouveautés" />}
                                    >
                                        <IconLinkWrapper
                                            as="a"
                                            href="https://updates.ausha.co/"
                                            target="_blank"
                                        >
                                            <FontAwesomeIcon
                                                icon={icon({
                                                    name: 'rocket-launch',
                                                    style: 'duotone',
                                                })}
                                                color="white"
                                                size="1x"
                                            />
                                        </IconLinkWrapper>
                                    </UiKitTooltip>
                                </>
                            )}
                        </IconsWrapper>
                    </Cluster>
                    {!isMobileOrTablet && <Separator />}
                    <OutsideClickHandler onOutsideClick={() => setDropdownMenuOpened(false)}>
                        <Cluster $gap="0.5rem" $align="center" $wrap="nowrap">
                            <Cluster
                                $gap="0.25rem"
                                onClick={() => {
                                    setDropdownMenuOpened(!dropdownMenuOpened);
                                }}
                                $wrap="nowrap"
                                $align="center"
                                data-testid="dropdown-user-menu-link"
                            >
                                {subscription?.isFreemium && (
                                    <Badge>
                                        <Text as="span" fontWeight="--fw-bold">
                                            FREE
                                        </Text>
                                    </Badge>
                                )}
                                {subscription?.pricing === PRICING.ENTERPRISE && (
                                    <Badge>
                                        <Text as="span" fontWeight="--fw-bold">
                                            PRO
                                        </Text>
                                    </Badge>
                                )}
                                {hasUser ? (
                                    <StyledText color="white" align="center">
                                        {user.data.firstname}
                                    </StyledText>
                                ) : (
                                    <TextSkeleton />
                                )}
                                <Chevron
                                    icon={icon({
                                        name: 'chevron-down',
                                        style: 'solid',
                                    })}
                                    color="white"
                                    size="xs"
                                    rotated={dropdownMenuOpened}
                                />
                            </Cluster>
                        </Cluster>
                        <UserDropdownMenu
                            visible={dropdownMenuOpened}
                            onClose={() => setDropdownMenuOpened(false)}
                            onOpenFeedback={() => setFeedbackOpened(true)}
                        />
                    </OutsideClickHandler>
                </MenuIconsWraper>
            </AushaHeaderMenuWrapper>
            <FeedbackModal isOpened={feedbackOpened} onClose={() => setFeedbackOpened(false)} />
            {aushaClubModalState.isOpen && (
                <AushaClubModal
                    isOpen={aushaClubModalState.isOpen}
                    onClose={aushaClubModalState.close}
                />
            )}
        </>
    );
};

const AushaHeaderMenuWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    height: 100%;
    padding-right: 1rem;
`;
const IconsWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    z-index: 1100;
`;
const IconLinkWrapper = styled(Link)`
    display: flex;
    align-items: center;
    justify-content: center;
`;
const StyledText = styled(Text)`
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    cursor: pointer;
    font-weight: var(--fw-semibold);
`;
const MenuIconsWraper = styled.div`
    height: 100%;
    display: flex;
    align-items: center;
    column-gap: 0.75rem;
`;
const Separator = styled.div`
    width: 2px;
    height: 1.25rem;
    background: var(--neutral100);
    background: #897dca;
`;
const TextSkeleton = styled.div`
    width: 6rem;
    height: 0.75rem;
    background-color: var(--white200);
    border-radius: var(--r-full);
    margin-block: 0.25rem;
`;
const Chevron = styled(FontAwesomeIcon)`
    transition-duration: 0.5s;
    transition-timing-function: var(--spring-easing);
    ${({ rotated }) =>
        rotated &&
        css`
            transform: rotate(-180deg);
        `}
`;

const Badge = styled.div`
    background: var(--white200);
    color: var(--white);
    padding: 0.3125rem 0.5rem;
    border-radius: var(--r-s);
    line-height: 0.875rem;
    cursor: pointer;
`;

export default AushaHeaderMenu;
